import React, { useEffect, useState } from 'react'
import {
  Button,
  Container,
  HtmlContent,
  Icon,
  State,
  TextBody,
  ToastContext
} from '@pmi/pmi-iqos-ds-react'
import {
  analytics,
  constants,
  cookie,
  dispatchPopupPromotionAlreadyUsed,
  setTranslations,
  CommonTranslations,
  updateCartCounter,
  getToastMessagesFromResponse,
  getToastMessagesFromError,
  extractErrorMessage,
  fillBundlesWithExtraData,
  getImageAltValue,
  isAuthenticated,
  isPurchaseLimitationsErrorCode,
  isCartPurchaseLimitationsErrorCode,
  extractErrorCode,
  parseTranslations,
  htmlTranslation,
  goTo,
  Routes,
  PublishedComponent
} from '@pmi/pmi-iqos-shared-react'
import { ProductBanner, ProductBundleDetailView } from '@dcx-common/components'
import { DcxApiManager } from '@dcx-common/services'
import ProductBundleDetailTranslations from './product-bundle-detail.json'

const ERR_NOT_AVAILABLE_ZIP = '124'

const ProductBundleDetailComponent = ({
  bundleData,
  translations,
  backgroundColor,
  crossSellBanner,
  bundleDetailPath,
  canonicalUrl,
  logError,
  line
}) => {
  const [bundle, setBundle] = useState(bundleData && bundleData.bundle)
  const [loading, setLoading] = useState(false)
  const [loadingButton, setLoadingButton] = useState(false)
  const [shortestDeliveryDate, setShortestDeliveryDate] = useState('')
  const [extraDataLoaded, setExtraDataLoaded] = useState(false)

  const toastContext = ToastContext.getContextActions()
  const popupToast = toastContext.popupToast
  const dismissToast = toastContext.dismissToast

  const api = new DcxApiManager(line)

  const listenLoginSuccessEvent = (kill = false) => {
    const e = constants.REACT_LOGIN_SUCCESS_EVENT
    const f = loadData
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  useEffect(() => {
    api.setErrorTranslations(translations)
    loadData()
  }, [])

  useEffect(() => {
    listenLoginSuccessEvent()
    return () => {
      listenLoginSuccessEvent(true)
    }
  }, [listenLoginSuccessEvent])

  if (translations) {
    setTranslations(translations)
  }

  const loadData = async () => {
    getBundleExtraData()
  }

  const getBundleExtraData = async () => {
    const params = {
      stock: true,
      promo: true,
      chainid: bundle.id
    }
    setExtraDataLoaded(false)
    setLoading(true)
    try {
      const res = await api.cart.getDevicesBundles({ params })
      const updatedChains = fillBundlesWithExtraData([bundle], res.chains)
      setBundle(
        updatedChains && updatedChains.length > 0 ? updatedChains[0] : null
      )

      getShortestDeliveryDate()
      dispatchPopupPromotionAlreadyUsed(
        cookie.getSpiceId(),
        updatedChains[0],
        popupToast,
        translations,
        Container,
        TextBody
      )
    } catch (err) {
      logError(err)
      throw err
    } finally {
      setLoading(false)
      setExtraDataLoaded(true)
    }
  }

  const getShortestDeliveryDate = async currentSelection => {
    const selectedProducts =
      currentSelection ||
      bundle.products.reduce((selectedProducts, product, index) => {
        selectedProducts[index] = product.selectedVariant
        return selectedProducts
      }, {})

    const inStock = (Object.keys(selectedProducts) || []).every(
      key => bundle.products[key].options[selectedProducts[key]].inStock
    )

    if (inStock) {
      if (isAuthenticated()) {
        try {
          const items = (Object.keys(selectedProducts) || []).map(key => ({
            code: bundle.products[key].options[selectedProducts[key]]
              .productCode,
            qty: 1
          }))
          const res = await api.cart.shortestDelivery(items)
          setShortestDeliveryDate(
            res.day && res.day.leadDays && res.timeSlots && res.timeSlots[0]
              ? res.timeSlots[0].label
              : ''
          )
        } catch (err) {
          if (err.data && err.data[0].code === ERR_NOT_AVAILABLE_ZIP) {
            setShortestDeliveryDate(translations.tk_defaultShortestDateValue)
          } else {
            setShortestDeliveryDate('')
          }
        }
      } else {
        setShortestDeliveryDate(translations.tk_defaultShortestDateValue)
      }
    } else {
      setShortestDeliveryDate('')
    }
  }

  const onColorChange = selectedProducts => {
    getShortestDeliveryDate(selectedProducts)
    dispatchPopupPromotionAlreadyUsed(
      cookie.getSpiceId(),
      {
        products: bundle.products.map((product, index) => {
          return { ...product, selectedVariant: selectedProducts[index] }
        })
      },
      popupToast,
      translations,
      Container,
      TextBody
    )
  }

  const cartFreeShippingToastContent = msg => {
    return (
      <Container display={Container.displays.flex} pb="2">
        <Container pr="2">
          <Icon size={Icon.sizes.s} image={Icon.images.orders} />
        </Container>
        <TextBody size={TextBody.sizes.s} acceptsHtml value={msg} />
      </Container>
    )
  }

  const handleAddToCart = async items => {
    try {
      setLoading(true)
      setLoadingButton(true)
      const res = await api.cart.addToCart(items)

      const showFamilyWarning = res.familyNotice && !cookie.getFamilyWarning()
      if (showFamilyWarning) {
        cookie.setFamilyWarning(true)
        analytics.productFamilyConflict(false)
      }

      const params = {
        promo: true,
        chainid: bundle.id
      }
      const result = await api.cart.getDevicesBundles({ params })
      setBundle({
        ...bundle,
        products: result.chains[0].products.map((product, index) => {
          return {
            ...bundle.products[index],
            options: product.options.map((option, indexOption) => ({
              ...bundle.products[index].options[indexOption],
              price: parseFloat(option.price),
              listPrice: parseFloat(option.list_price)
            }))
          }
        })
      })

      updateCartCounter(res.amount)

      analytics.addToCartBundle(bundle)

      popupToast({
        disclaimer: res.promotionDisclaimer,
        type: res.error ? ToastContext.types.error : ToastContext.types.none,
        title: res.error
          ? translations.tk_productAddedError
          : translations.tk_productAddedOk,
        subtitle: bundle.name,
        actions: res.error
          ? null
          : [
              <Button
                key={0}
                variant={Button.variants.secondary}
                text={translations.tk_toastKeepShopping}
                onClickHandler={() => handleKeepShopping(showFamilyWarning)}
                data-testing="ProductBundleDetail_Button_KeepShopping"
              />,
              <Button
                key={1}
                variant={Button.variants.primary}
                text={translations.tk_toastProceedToCart}
                onClickHandler={() => handleGoToCart(showFamilyWarning)}
                data-testing="ProductBundleDetail_Button_GoToCart"
              />
            ],
        content: (
          <Container>
            {res.free_shipping_message &&
              cartFreeShippingToastContent(res.free_shipping_message)}
            {showFamilyWarning && (
              <Container
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.row}
                alignItems={Container.alignItemsValues.center}
                pb="4">
                <Container pr="4">
                  <State size={State.sizes.s} state={State.states.warningRed} />
                </Container>
                <TextBody
                  style={TextBody.styles.error}
                  {...htmlTranslation(
                    translations,
                    'tk_addedToCartFamilyNotice'
                  )}
                />
              </Container>
            )}
            <TextBody size={TextBody.sizes.s}>
              {getToastMessagesFromResponse(res)}
            </TextBody>
          </Container>
        )
      })
    } catch (err) {
      const errorCode = extractErrorCode(err)
      const isPLError = isPurchaseLimitationsErrorCode(errorCode)
      if (isPLError) {
        analytics.addToCartLimitations(
          bundle && bundle.id,
          bundle && bundle.name,
          isCartPurchaseLimitationsErrorCode(errorCode)
        )
      }
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_productAddedError,
        subtitle: bundle && bundle.name,
        content: (
          <Container>
            <TextBody
              size={TextBody.sizes.s}
              acceptsHtml
              value={getToastMessagesFromError(err)}
            />
            {isPLError && (
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(
                  translations,
                  'tk_purchaseLimitationsErrorTermsAndConditions'
                )}
              />
            )}
          </Container>
        )
      })
    } finally {
      setLoading(false)
      setLoadingButton(false)
    }
  }

  const handleGoToCart = familyWarning => {
    familyWarning && analytics.productFamilyConflictDecision(true)
    goTo(Routes.cart, true)
  }

  const handleKeepShopping = familyWarning => {
    familyWarning && analytics.productFamilyConflictDecision(false)
    dismissToast()
  }

  return (
    <>
      {bundle && bundle.promoBanner && (
        <ProductBanner image={bundle.promoBanner} />
      )}
      {bundle && (
        <ProductBundleDetailView
          bundle={bundle}
          translations={translations}
          loading={loading}
          loadingButton={loadingButton}
          extraDataLoaded={extraDataLoaded}
          handleAddToCart={handleAddToCart}
          handleColorChange={onColorChange}
          shortestDeliveryDate={shortestDeliveryDate}
          backgroundColor={backgroundColor}
          bundleDetailPath={bundleDetailPath}
          canonicalUrl={canonicalUrl}
          line={line}
        />
      )}
      {crossSellBanner && (
        <HtmlContent
          className="cross-sell-banner-container"
          fullWidth
          content={crossSellBanner}
        />
      )}
    </>
  )
}

const ProductBundleDetail = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProductBundleDetailComponent}
        mainLayout
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProductBundleDetailTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProductBundleDetail',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProductBundleDetail.getBundle = async (chainid, req, line) => {
  const api = new DcxApiManager(line)
  const res = await api.cart.getDevicesBundles({
    req,
    params: {
      chainid
    }
  })

  const chain = res.chains[0]
  const allvariants = chain.products
    ? chain.products.map(product => {
        return {
          id: product.id,
          name: product.name,
          color: product.hex_color,
          colorName: product.color_name,
          image: product.image_path,
          description: product.full_description,
          inStock: product.total_stock > 0,
          selectedVariant: product.selected_variant || 0,
          family: product.family,
          options:
            product.options &&
            product.options.map(option => {
              return {
                id: option.option_id,
                optionName: option.option_name,
                variantId: option.variant_id,
                variantName: option.variant_name,
                shortVariantName: option.short_variant_name,
                color: option.hex_color,
                image: option.images &&
                  option.images[0] && {
                    path: option.images[0].image_path || null,
                    alt: getImageAltValue(
                      option.images[0].alt,
                      option.variant_name
                    )
                  },
                promotionalBadge: option.promotional_badge,
                inStock: option.stock > 0,
                productCode: option.product_code,
                price: parseFloat(option.price),
                listPrice: parseFloat(option.list_price),
                discount: option.discount || 0,
                description: option.full_description,
                descriptionLine: option.full_description_line,
                badges: option.badges
              }
            })
        }
      })
    : []

  const bundle = {
    id: chain.chain_id,
    categoryId: chain.category_id,
    category: chain.main_category_name,
    name: chain.name,
    currency: chain.currency,
    shortDescription: chain.short_description,
    fullDescription: chain.full_description,
    descriptionLine: chain.full_description_line,
    products: allvariants.slice(0, Math.min(allvariants.length, 3)),
    promoBanner: chain.promotional_banners[0],
    badge: chain.badge,
    seoTitle: chain.seo_title,
    seoDescription: chain.seo_description,
    imagePath: chain.img_path
  }

  return {
    bundle
  }
}

ProductBundleDetail.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const chainid = req.query.chainid
      const line = !!req?.query?.line
      let bundleData
      try {
        bundleData = await ProductBundleDetail.getBundle(chainid, req, line)
      } catch (err) {
        const message = extractErrorMessage(err)
        // since API GW does not send us a proper 404 we need to treat in a special way this error
        // in order for Drupal to send a proper 404 whille doins SSR
        if (message === 'Bundles not available') {
          return {
            error: {
              message,
              status: 404
            }
          }
        } else {
          throw err
        }
      }
      return {
        extras: {
          main_category: bundleData.bundle.categoryId || null,
          title: bundleData.bundle.name,
          seo_title: bundleData.bundle.seoTitle,
          seo_description: bundleData.bundle.seoDescription
        },
        bundleData,
        crossSellBanner:
          (req.body.extra && req.body.extra.crossSellBannerPdp) || null,
        backgroundColor:
          (req.body.extra && req.body.extra.background_color) ||
          PublishedComponent.backgrounds.white,
        bundleDetailPath:
          (req.body.extra && req.body.extra.bundle_detail_path) || '',
        canonicalUrl: req.body.extra && req.body.extra.canonicalURL
      }
    }
  })
}

export default ProductBundleDetail
